import type { Locale } from '@ps-smart-cp/ppfw-client';

type Enum = Record<number, string> | Record<string, number> | Record<string, string>;
type NameConfig<T> = Record<keyof T, Map<Locale, string>>;

const trans = (l10nText: Map<Locale, string>, locale: Locale, defVal = '') => l10nText.get(locale) ?? defVal;

const enumToName = <T extends Enum>(
  enumObj: T,
  nameConfig: NameConfig<T>,
  options?: { prefix?: boolean; defVal?: string },
) => {
  const enumEntries = Object.entries(enumObj);

  return (val: number | string, locale: Locale) => {
    if (!val) return '';

    const enumKey = enumEntries.find(([_, v]) => v === val)?.[0];

    if (!enumKey) throw new Error(`'${val}' do not exist in the enum.`);

    return (
      (options?.prefix ? `${val}   ` : '') +
      trans(nameConfig[enumKey as keyof T], locale, options?.defVal ?? '')
    );
  };
};

function getEnumValues(e: Enum) {
  const keys = Object.keys(e);

  return Object.values(e).filter((v: number | string) => typeof v === 'number' || !keys.includes(v)) as
    | number[]
    | string[];
}

function rawValueToEnum<T extends Enum, Default extends string>(nativeEnum: T, defaultValue: Default) {
  return (value: unknown) => {
    return getEnumValues(nativeEnum).includes(value as never) ? (value as T[keyof T]) : defaultValue;
  };
}

export type { Enum };
export { enumToName, rawValueToEnum };
